import { useState, useRef } from "react";
import { useShouldPrint } from "~/components/hooks/useShouldPrint";
import type { SummaryItem } from "~/services/summary/types";
import SummarySection from "./SummarySection";
import Button from "~/components/common/Button";
import { useJsAvailable } from "~/components/hooks/useJsAvailable";
import { translations } from "~/services/translations/translations";

type CollapsibleProps = {
  items: SummaryItem[];
  showGlobalControls?: boolean;
};

const AutoGeneratedSummary = ({
  items,
  showGlobalControls = true,
}: Readonly<CollapsibleProps>) => {
  const shouldPrint = useShouldPrint();
  const itemsRef = useRef<HTMLDetailsElement[]>([]);
  const jsAvailable = useJsAvailable();

  const [openSections, setOpenSections] = useState<Set<string | number>>(() => {
    // Open first section by default
    if (items.length > 0) {
      return new Set([items[0].id ?? 0]);
    }
    return new Set();
  });

  const allOpen = openSections.size === items.length;
  const noneOpen = openSections.size === 0;

  const expandAll = () => {
    if (allOpen) return;
    for (const item of itemsRef.current) {
      if (item) item.open = true;
    }
    setOpenSections(new Set(items.map((item, index) => item.id ?? index)));
  };

  const collapseAll = () => {
    if (noneOpen) return;
    for (const item of itemsRef.current) {
      if (item) item.open = false;
    }
    setOpenSections(new Set());
  };

  const handleSectionToggle = (itemId: string | number, isOpen: boolean) => {
    const newOpenSections = new Set(openSections);
    if (isOpen) {
      newOpenSections.add(itemId);
    } else {
      newOpenSections.delete(itemId);
    }
    setOpenSections(newOpenSections);
  };

  if (items.length === 0) {
    return null;
  }

  const ariaLabelExpandAll =
    translations.autogeneratedSummary.expandAllText.de +
    (allOpen
      ? ", " + translations.autogeneratedSummary.expandAllAriaLabelText.de
      : "");

  const ariaLabelCollapseAll =
    translations.autogeneratedSummary.collapseAllText.de +
    (noneOpen
      ? ", " + translations.autogeneratedSummary.collapseAllAriaLabelText.de
      : "");

  return (
    <div>
      {showGlobalControls && jsAvailable && (
        <div className="flex gap-kern-space-default my-kern-space-large">
          <Button
            look="secondary"
            onClick={expandAll}
            text={translations.autogeneratedSummary.expandAllText.de}
            aria-disabled={allOpen}
            aria-expanded={allOpen}
            className={allOpen ? "kern-btn--disabled pointer-events-none" : ""}
            aria-label={ariaLabelExpandAll}
          />
          <Button
            look="secondary"
            onClick={collapseAll}
            text={translations.autogeneratedSummary.collapseAllText.de}
            aria-disabled={noneOpen}
            aria-expanded={!noneOpen}
            className={noneOpen ? "kern-btn--disabled pointer-events-none" : ""}
            aria-label={ariaLabelCollapseAll}
          />
        </div>
      )}

      <div>
        {items.map((item, index) => {
          const itemId = item.id ?? index;
          const startOpened = shouldPrint || openSections.has(itemId);

          return (
            <SummarySection
              key={itemId}
              item={item}
              itemId={itemId}
              startOpened={startOpened}
              onToggle={handleSectionToggle}
            />
          );
        })}
      </div>
    </div>
  );
};

export default AutoGeneratedSummary;
